import React, { useCallback, useRef, useState } from "react";

import { colorSwatches } from "./colors";
import { normalizeRouteColor, resolveRouteColorPalette } from "./routeColor";
import type { RouteColorName } from "../../../types/routeBuilder";
import { useDismissiblePopover } from "../../Inputs/useDismissiblePopover";

/** Compact swatch that opens the named Active Routes color palette. */
export default function RouteColorSwatch({
  color,
  onChange,
  size = "md",
  label,
  placement = "right",
  testId,
  stopPropagation = false,
}: {
  color: RouteColorName | string;
  onChange: (color: RouteColorName) => void;
  size?: "sm" | "md" | "lg";
  label?: string;
  placement?: "left" | "right";
  testId?: string;
  stopPropagation?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const normalizedColor = normalizeRouteColor(color);
  const palette = resolveRouteColorPalette(normalizedColor);
  const dim =
    size === "sm"
      ? "h-2.5 w-2.5"
      : size === "lg"
        ? "h-10 w-10"
        : "h-3.5 w-3.5";
  const corner =
    size === "sm" ? "rounded-sm" : size === "lg" ? "rounded-lg" : "rounded-md";
  const palettePosition =
    placement === "left"
      ? "absolute right-full top-0 z-30 mr-2"
      : "absolute left-full top-0 z-30 ml-2";

  const dismiss = useCallback(() => setOpen(false), []);
  useDismissiblePopover(open, rootRef, dismiss);

  const palettePanel = open ? (
    <div
      className={`${palettePosition} grid min-w-[12.5rem] grid-cols-5 gap-2 rounded-xl border border-slate-200 bg-white p-3 shadow-lg`}
      data-testid={testId ? `${testId}-palette` : undefined}
      onClick={(e) => {
        if (stopPropagation) e.stopPropagation();
      }}
    >
      {colorSwatches.map((swatchOption) => {
        const selected = swatchOption.name === normalizedColor;
        const swatchPalette = resolveRouteColorPalette(swatchOption.name);
        return (
          <button
            key={swatchOption.name}
            type="button"
            data-testid={
              testId ? `${testId}-option-${swatchOption.name}` : undefined
            }
            title={swatchOption.name}
            aria-label={swatchOption.name}
            aria-pressed={selected}
            className={`h-7 w-7 ${swatchPalette.bg} rounded-md border border-slate-200 transition-transform hover:scale-110 ${
              selected
                ? "ring-2 ring-snow-primary ring-offset-2"
                : "hover:ring-2 hover:ring-slate-300 hover:ring-offset-1"
            }`}
            onClick={(e) => {
              if (stopPropagation) e.stopPropagation();
              onChange(swatchOption.name as RouteColorName);
              setOpen(false);
            }}
          />
        );
      })}
    </div>
  ) : null;

  const swatchControl = (
    <div ref={rootRef} className="relative inline-flex shrink-0">
      <button
        type="button"
        data-testid={testId}
        data-color={normalizedColor}
        title="Change color"
        aria-label="Change color"
        aria-expanded={open}
        className={`${dim} ${palette.bg} shrink-0 ${corner} border border-slate-300 shadow-sm outline-none ring-offset-1 hover:ring-2 hover:ring-sky-200 focus-visible:ring-2 focus-visible:ring-sky-300`}
        onClick={(e) => {
          if (stopPropagation) e.stopPropagation();
          setOpen((v) => !v);
        }}
      />
      {palettePanel}
    </div>
  );

  if (label) {
    return (
      <div className="mb-5 w-full text-snow-primary">
        <div className="text-sm font-bold">{label}</div>
        <div className="mt-2">{swatchControl}</div>
      </div>
    );
  }

  return swatchControl;
}
